/**
 * SidebarIcons — lucide-style 15px stroke outlines for the decluttered rail.
 *
 * Prototype `pipeline.html` line 54: nav icons are 15px, 1.5 stroke,
 * `opacity:.7` (→ `opacity:1` when active). These are hand-rolled (no new
 * dependency) and inherit `currentColor` so the active/inactive text-color
 * treatment on the link drives them. Size + opacity are applied by the caller
 * via `className` so the rail owns the visual register.
 *
 * Each icon is decorative (`aria-hidden` set by the caller); the link text /
 * `aria-label` carries the accessible name.
 */
import type { JSX } from "react";

export interface IconProps {
  className?: string;
  "aria-hidden"?: boolean | "true" | "false";
}

function svg(path: JSX.Element) {
  return function Icon({ className, ...rest }: IconProps): JSX.Element {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...rest}
      >
        {path}
      </svg>
    );
  };
}

// Dashboard — grid of four panes.
export const DashboardIcon = svg(
  <>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </>,
);

// All Candidates — people.
export const CandidatesIcon = svg(
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </>,
);

// Manage Jobs — briefcase.
export const JobsIcon = svg(
  <>
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </>,
);

// Hiring Intelligence — bar chart / analytics.
export const IntelligenceIcon = svg(
  <>
    <path d="M3 3v18h18" />
    <path d="M7 16l4-5 3 3 4-6" />
  </>,
);

// Hiring Journal — book / log.
export const JournalIcon = svg(
  <>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </>,
);

// Outcomes — activity pulse (post-placement tracking).
export const OutcomesIcon = svg(<path d="M22 12h-4l-3 9L9 3l-3 9H2" />);

// Help — question mark in a circle (Help Center entry point).
export const HelpIcon = svg(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </>,
);

// Settings — gear.
export const SettingsIcon = svg(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </>,
);
